import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalendarPlus,
  IconCheck,
  IconClock,
  IconMapPin,
  IconStethoscope,
  IconUser,
} from '@tabler/icons-react';
import { useState } from 'react';

const API_URL = import.meta.env.VITE_CALENDAR_API_URL || 'https://cal.hillsideharmony.com';

interface AppointmentFormValues {
  resident_name: string;
  date: Date | null;
  time: string;
  provider: string;
  location: string;
  notes: string;
  duration_minutes: number;
}

export function AddAppointmentPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<AppointmentFormValues>({
    initialValues: {
      resident_name: '',
      date: null,
      time: '',
      provider: '',
      location: '',
      notes: '',
      duration_minutes: 60,
    },
    validate: {
      resident_name: (value) => (value.trim().length < 2 ? 'Resident name is required' : null),
      date: (value) => (!value ? 'Date is required' : null),
      time: (value) => (!value ? 'Time is required' : null),
    },
  });

  const handleSubmit = async (values: AppointmentFormValues) => {
    if (!values.date) return;

    setIsSubmitting(true);
    try {
      const dateStr = values.date.toISOString().split('T')[0]; // YYYY-MM-DD

      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resident_name: values.resident_name,
          date: dateStr,
          time: values.time,
          provider: values.provider || null,
          location: values.location || null,
          notes: values.notes || null,
          duration_minutes: values.duration_minutes,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create appointment');
      }

      setSubmitted(true);
      form.reset();
      notifications.show({
        title: 'Appointment Created',
        message: `Appointment for ${values.resident_name} has been added to the calendar.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Reset form for another entry after a delay
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create appointment',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box py={60}>
      <Container size="sm">
        <Stack gap="xl">
          <Stack align="center" gap="xs">
            <Title order={2}>Add Appointment</Title>
            <Text c="dimmed" ta="center">
              Add a resident appointment to the shared calendar.
              <br />
              Staff will be notified in #appointments.
            </Text>
          </Stack>

          <Card padding="xl" shadow="sm" radius="lg">
            {submitted ? (
              <Alert
                icon={<IconCheck size={16} />}
                title="Appointment Added!"
                color="green"
                variant="light"
              >
                The appointment has been added to the calendar and staff have been notified.
              </Alert>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  <TextInput
                    label="Resident Name"
                    placeholder="Mrs. Johnson"
                    required
                    leftSection={<IconUser size={16} />}
                    {...form.getInputProps('resident_name')}
                  />

                  <DateInput
                    label="Date"
                    placeholder="Select date"
                    required
                    minDate={new Date()}
                    leftSection={<IconCalendarPlus size={16} />}
                    {...form.getInputProps('date')}
                  />

                  <TimeInput
                    label="Time"
                    placeholder="2:00 PM"
                    required
                    leftSection={<IconClock size={16} />}
                    {...form.getInputProps('time')}
                  />

                  <NumberInput
                    label="Duration (minutes)"
                    placeholder="60"
                    min={15}
                    max={480}
                    step={15}
                    {...form.getInputProps('duration_minutes')}
                  />

                  <TextInput
                    label="Provider / Doctor"
                    placeholder="Dr. Smith, Cardiology"
                    leftSection={<IconStethoscope size={16} />}
                    {...form.getInputProps('provider')}
                  />

                  <TextInput
                    label="Location"
                    placeholder="Mayo Clinic, 200 First St SW"
                    leftSection={<IconMapPin size={16} />}
                    {...form.getInputProps('location')}
                  />

                  <Textarea
                    label="Notes"
                    placeholder="Needs wheelchair transport, fasting required, etc."
                    minRows={2}
                    {...form.getInputProps('notes')}
                  />

                  <Button
                    type="submit"
                    size="lg"
                    leftSection={<IconCalendarPlus size={18} />}
                    loading={isSubmitting}
                    fullWidth
                    mt="md"
                  >
                    Add to Calendar
                  </Button>
                </Stack>
              </form>
            )}
          </Card>

          <Text c="dimmed" size="sm" ta="center">
            This form adds appointments to the shared Hillside Harmony calendar.
            <br />
            For appointments received by email, they are added automatically.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
